import {
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxIdWebFeaturesGamesChooseGameResponsePublicGameData
} from "@app/shared/api/gen";
import { UseOSReturnValue } from "@mantine/hooks";
import { FxIdSdkAdapterForCrazyGames } from "@sdk/FxIdSdkAdapterForCrazyGames.ts";
import { FxIdSdkAdapterForFacebookGames } from "@sdk/FxIdSdkAdapterForFacebookGames.ts";
import { FxIdSdkAdapterForFxId } from "@sdk/FxIdSdkAdapterForFxId.ts";
import { FxIdSdkAdapterForKongregate } from "@sdk/FxIdSdkAdapterForKongregate.ts";
import { FxIdSdkAdapterForOk } from "@sdk/FxIdSdkAdapterForOk.ts";
import { FxIdSdkAdapterForTelegram } from "@sdk/FxIdSdkAdapterForTelegram.ts";
import { FxIdSdkAdapterForVkCom } from "@sdk/FxIdSdkAdapterForVkCom.ts";
import { FxIdSdkAdapterForVkPlay } from "@sdk/FxIdSdkAdapterForVkPlay.ts";
import { FxIdSdkAdapterForYandexGames } from "@sdk/FxIdSdkAdapterForYandexGames.ts";

import { FxIdSdkAdapterForAbsoluteGames } from "./FxIdSdkAdapterForAbsoluteGames.ts";
import { FxIdSdkAdapterForFacebookInstantGames } from "./FxIdSdkAdapterForFacebookInstantGames.tsx";
import { FxIdSdkAdapterForWizQ } from "./FxIdSdkAdapterForWizQ.ts";
import type { FxIdSdkBaseAdapter } from "./FxIdSdkBaseAdapter";
import type { IExportedFxIdSdkMethod } from "./FxIdSdkTypes.ts";

/**
 * @fileoverview Фабрика для создания экземпляров адаптеров SDK.
 * Разрывает циклическую зависимость между FxIdSdk и конкретными адаптерами.
 */

/**
 * Создает и возвращает экземпляр нужного адаптера на основе платформы.
 * @param platform - Платформа, для которой нужно создать адаптер.
 * @param exportedSdk - Экземпляр FxIdSdk для передачи в конструктор адаптера.
 * @param game - Системное имя игры.
 * @param gameConfig - Конфигурация игры.
 * @param os - Информация об операционной системе.
 * @param isMobile - Флаг, указывающий, является ли устройство мобильным.
 * @returns {Promise<FxIdSdkBaseAdapter>} - Созданный экземпляр адаптера.
 */
export async function createAdapter(
	platform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	exportedSdk: IExportedFxIdSdkMethod,
	game: string,
	gameConfig: FxIdWebFeaturesGamesChooseGameResponsePublicGameData,
	os: UseOSReturnValue,
	isMobile: boolean
): Promise<FxIdSdkBaseAdapter> {
	switch (platform) {
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Odnoklassniki: {
			return new FxIdSdkAdapterForOk(exportedSdk, game, gameConfig, os, isMobile);
		}
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontaktePlay: {
			return new FxIdSdkAdapterForVkPlay(exportedSdk, game, gameConfig);
		}
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontakteCom: {
			return new FxIdSdkAdapterForVkCom(exportedSdk, game, gameConfig);
		}
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxId: {
			return new FxIdSdkAdapterForFxId(exportedSdk, game, gameConfig);
		}
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.YandexGames: {
			return new FxIdSdkAdapterForYandexGames(exportedSdk, game, gameConfig);
		}
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.AbsoluteGames: {
			return new FxIdSdkAdapterForAbsoluteGames(exportedSdk, game, gameConfig);
		}
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Kongregate: {
			return new FxIdSdkAdapterForKongregate(exportedSdk, game, gameConfig);
		}
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookGames: {
			return new FxIdSdkAdapterForFacebookGames(exportedSdk, game, gameConfig);
		}
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookInstantGames: {
			return new FxIdSdkAdapterForFacebookInstantGames(exportedSdk, game, gameConfig);
		}
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.CrazyGames: {
			return new FxIdSdkAdapterForCrazyGames(exportedSdk, game, gameConfig);
		}
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.WizQ: {
			return new FxIdSdkAdapterForWizQ(exportedSdk, game, gameConfig);
		}
		case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Telegram: {
			return new FxIdSdkAdapterForTelegram(exportedSdk, game, gameConfig);
		}
		default:
			throw new Error(`Unsupported adapter for ${platform}`);
	}
}
